"use client"

import React, {useCallback, useEffect, useMemo, useState} from "react"
import {Button} from "@/components/ui/button"
import {MoveRight, SearchIcon} from "lucide-react"
import {Input} from "@/components/ui/input"
import {AdvancedSearchService} from "@/http/api/search/AdvancedSearch"
import useDebounce from "@/hook/useDebounce"
import ProductResult from "@/components/ui/search/search-results/product-result"
import RelatedResult from "@/components/ui/search/search-results/related-result"
import BrandResult from "@/components/ui/search/search-results/brand-result"
import CategoryResult from "@/components/ui/search/search-results/category-result"
import Spinner from "@/components/ui/loading/Spinner";
import {useRouter} from "next/navigation";
import {DataLayerFactory} from "@/components/google-tag-manager/DataLayerFactory";

const POPULAR_SEARCHES = ["شیگلم", "ضد آفتاب", "کرم مرطوب کننده", "ماسک صورت", "شامپو"]

const {advancedSearch} = new AdvancedSearchService()

function MobileMenuSearchResult() {

    const [open, setOpen] = useState(false)
    const [searchValue, setSearchValue] = useState("")
    const [data, setData] = useState<any>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const debouncedSearchQuery = useDebounce(searchValue, 700)
    const router = useRouter()

    useEffect(() => {
        const fetchSearchData = async () => {
            if (debouncedSearchQuery.trim()) {
                setLoading(true)
                setError(null)
                try {
                    const response: any = await advancedSearch(debouncedSearchQuery)

                    setData(response)
                } catch (error) {
                    console.error("Error fetching search results:", error)
                    setError("Failed to fetch search results")
                } finally {
                    setLoading(false)
                }
            } else {
                setData(null)
            }
        }
        fetchSearchData()
    }, [debouncedSearchQuery])

    const toggleOpen = useCallback(() => setOpen((prev) => !prev), [])

    const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.target.value)
    }, [])

    const handlePopularSearchClick = useCallback((popularSearch: string) => {
        DataLayerFactory.make('search', popularSearch as string);
        router.push(`/search?search=${encodeURIComponent(popularSearch as string)}`)
        setSearchValue(popularSearch)
        setOpen(false)
    }, [])

    const hasValue = useMemo(() => searchValue.length > 2, [searchValue])

    if (!open) {
        return (
            <Button
                onClick={toggleOpen}
                className="!bg-transparent p-0 m-0"
                aria-label="search-mobile-button"
                variant="ghost"
            >
                <SearchIcon className="w-6 h-6"/>
            </Button>
        )
    }

    const handleEnterEvent = (e : any) => {
        if (e.key === "Enter") {
            router.push("/search?search=" + e.target.value)
            setOpen(false)
        }
    }


    return (
        <section
            className="w-full search-show-anime bg-white flex content-start flex-wrap left-0 h-screen overflow-y-auto fixed z-auto top-0 p-4">
            <div className="w-full flex flex-wrap items-center justify-between">
                <Button onClick={toggleOpen} variant="none" className="p-1 rounded bg-transparent">
                    <MoveRight className="w-6 h-6"/>
                </Button>
                <div className="flex-1 flex-wrap relative p-2">
                    <Input
                        value={searchValue}
                        onChange={handleInputChange}
                        onKeyDown={handleEnterEvent}
                        className="w-full border shadow-none rounded-xl h-12 ps-10"
                        placeholder="جستجو..."
                    />
                    <SearchIcon className="w-5 h-5 absolute top-1/2 -translate-y-1/2 right-5"/>
                </div>
            </div>

            {loading ? (<div className={"min-h-[400px] flex items-center justify-center"}><Spinner/></div>) : (
                <>
                    {error && <div>Error: {error}</div>}

                    {hasValue && data ? (
                        <ResultSearchModal data={data}/>
                    ) : (
                        <DefaultSearchResult onPopularSearchClick={handlePopularSearchClick}/>
                    )}
                </>
            )}

        </section>
    )
}

const ResultSearchModal = React.memo(({data}: { data: any }) => (
    <div className="w-full space-y-6">
        <ProductResult data={data}/>
        <RelatedResult data={data}/>
        <BrandResult data={data}/>
        <CategoryResult data={data}/>
    </div>
))

interface DefaultSearchResultProps {
    onPopularSearchClick: (popularSearch: string) => void
}

const DefaultSearchResult: React.FC<DefaultSearchResultProps> = React.memo(({onPopularSearchClick}) => (
    <div className="w-full flex flex-wrap my-5">
        <h2 className="w-full font-bold text-base">جستجو های پرطرفدار</h2>
        <div className="w-full mt-3 flex overflow-x-auto gap-5">
            {POPULAR_SEARCHES.map((search, index) => (
                <div
                    key={index}
                    className="h-[42px] text-sm border rounded-2xl w-auto p-2.5 whitespace-nowrap cursor-pointer"
                    onClick={() => onPopularSearchClick(search)}
                >
                    {search}
                </div>
            ))}
        </div>
        <div className="w-full h-80 flex content-center justify-center flex-wrap">
            <SearchIcon className="text-orange-primary h-12 w-12"/>
            <div className="w-full flex items-center justify-center mt-5 text-xs">محصول مورد نظر خود را جستجو کنید</div>
        </div>
    </div>
))

MobileMenuSearchResult.displayName = "MobileMenuSearchResult"
ResultSearchModal.displayName = "ResultSearchModal"
DefaultSearchResult.displayName = "DefaultSearchResult"

export default MobileMenuSearchResult

